import { type UseMutationOptions, useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import toast from 'react-hot-toast'
import {
	type HttpAllocateImagesOptions,
	httpAllocateImages
} from '@/features/photoset/http/photoset.http'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import { generateToastAllocationId } from '@/features/photoset/utils.ts'
import { raise } from '@/helpers/utils'
import { MUTATION_KEYS } from '@/query-client'
import { UndoMessage } from '../components/undo-message'

export const useAllocateImages = ({
	onUndo,
	showToast = false,
	options
}: {
	onUndo: (imagesIds: number[], spaceId: number, isShortcut: boolean) => void
	showToast?: boolean
	options?: Omit<
		UseMutationOptions<unknown, AxiosError, HttpAllocateImagesOptions>,
		'mutationFn' | 'mutationKey'
	>
}) => {
	const photosetSpaces = usePhotosetStore((store) => store.photosetSpacesDict)
	const allocateImages = usePhotosetStore((store) => store.allocateImages)

	return useMutation({
		...options,
		mutationKey: [MUTATION_KEYS.ALLOCATE_IMAGES],
		mutationFn: httpAllocateImages,
		onSuccess: (data, variables, onMutateResult, context) => {
			allocateImages({
				imagesIds: variables.payload,
				photosetSpaceId: variables.urlParams.photosetSpaceId,
				keepSelected: false,
				trackInHistory: true
			})

			if (showToast) {
				const spaceName =
					photosetSpaces[variables.urlParams.photosetSpaceId]?.space
						.key_name ??
					raise(
						`No space found with id ${variables.urlParams.photosetSpaceId}`
					)

				const toastId = generateToastAllocationId(
					'allocation',
					variables.urlParams.photosetSpaceId,
					variables.payload
				)

				toast.success(
					<UndoMessage
						onUndo={() =>
							onUndo(
								variables.payload,
								variables.urlParams.photosetSpaceId,
								false
							)
						}
						operationType='allocation'
						spaceKeyName={spaceName}
					/>,
					{ duration: 8000, id: toastId }
				)
			}

			options?.onSuccess?.(data, variables, onMutateResult, context)
		}
	})
}
